import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/i18n";
import { useProjects, useServices } from "../../lib/queries";
import { optimizeImg } from "../../lib/img";

export default function Projects() {
  const { data: projects = [] } = useProjects();
  const { data: services = [] } = useServices();
  const [filter, setFilter] = useState("all");
  const { t, pick } = useLang();

  const filtered = useMemo(() => {
    if (filter === "all") return projects;
    return projects.filter(
      (p) => p.category === filter || p.services?.includes(filter),
    );
  }, [projects, filter]);

  return (
    <section id="projects" data-testid="projects-section" className="py-32 relative bg-[#0A0A11] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#A855F7]" />
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
                {t("projects_kicker")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white">
              {t("projects_title")}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors border ${
                filter === "all"
                  ? "bg-[#A855F7] border-[#A855F7] text-white"
                  : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
              }`}
            >
              {t("projects_all")}
            </button>
            {services.slice(0, 6).map((s) => (
              <button
                key={s.slug}
                data-testid={`filter-${s.slug}`}
                onClick={() => setFilter(s.slug)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors border ${
                  filter === s.slug
                    ? "bg-[#A855F7] border-[#A855F7] text-white"
                    : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {pick(s, "title")}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.3) }}
              >
                <Link
                  to={`/projects/${p.slug}`}
                  data-testid={`project-card-${p.slug}`}
                  className="group block relative overflow-hidden rounded-2xl border border-white/5 hover:border-[var(--accent-bright,#A855F7)]/50 transition-colors contain-paint"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={optimizeImg(p.cover_image, 800, 72)}
                      alt={p.brand_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#08080D] via-[#08080D]/50 to-transparent" />
                    {p.video_url && (
                      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center opacity-90">
                        <Play size={14} fill="white" />
                      </div>
                    )}
                    <div className="absolute top-4 left-4 flex items-center gap-2 font-mono-ui text-[10px] tracking-widest uppercase text-white/80">
                      <span className="px-2 py-1 rounded-full bg-black/50 border border-white/10">
                        {p.category}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="text-[10px] font-mono-ui tracking-widest text-[#A855F7] uppercase mb-2">
                        {p.brand_name}
                      </div>
                      <h3 className="font-display text-2xl uppercase tracking-tight text-white leading-tight">
                        {pick(p, "title")}
                      </h3>
                      <div className="mt-3 flex items-center gap-2 text-white/70 group-hover:text-[#A855F7] transition-colors">
                        <span className="text-[11px] uppercase tracking-widest">
                          {t("project_watch")}
                        </span>
                        <ArrowUpRight size={12} className="transition-transform group-hover:rotate-45" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
